import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { baseUrl } from "../utils/Constants";

const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: token } };
};

export const fetchQuestionById = createAsyncThunk(
  "queriesAnswer/fetchQuestionById",
  async (questionId, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${baseUrl}/questions/${questionId}`,
        getAuthHeader(),
      );

      return res.data;
    } catch {
      return rejectWithValue("Failed to fetch question");
    }
  },
);

export const fetchAnswers = createAsyncThunk(
  "queriesAnswer/fetchAnswers",
  async (questionId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${baseUrl}/fetchAnswers`, {
        ...getAuthHeader(),
        params: { questionId },
      });
      return res.data;
    } catch {
      return rejectWithValue("Failed to fetch answers");
    }
  },
);

export const postAnswer = createAsyncThunk(
  "queriesAnswer/postAnswer",
  async ({ questionId, answer }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseUrl}/postAnswer`,
        { questionId, answer },
        getAuthHeader(),
      );

      return res.data.answer;
    } catch {
      return rejectWithValue("Failed to post answer");
    }
  },
);

export const likeAnswer = createAsyncThunk(
  "queriesAnswer/likeAnswer",
  async ({ answer }, { rejectWithValue }) => {
    try {
      await axios.patch(
        `${baseUrl}/likeAnswer`,
        { answerId: answer.answerid },
        getAuthHeader(),
      );

      return {
        answerId: answer.answerid,
      };
    } catch (err) {
      if (err.response?.status === 409) {
        return rejectWithValue("Already liked");
      }
      return rejectWithValue("Like failed");
    }
  },
);

export const markBestAnswer = createAsyncThunk(
  "queriesAnswer/markBest",
  async ({ questionId, answerId }, { dispatch }) => {
    await axios.patch(
      `${baseUrl}/markBestAnswer`,
      { questionId, answerId },
      getAuthHeader(),
    );
    dispatch(fetchAnswers(questionId));
  },
);

export const fetchAISettings = createAsyncThunk(
  "queriesAnswer/fetchAISettings",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${baseUrl}/user/ai-settings`,
        getAuthHeader(),
      );

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch AI settings",
      );
    }
  },
);

export const updateAISettings = createAsyncThunk(
  "queriesAnswer/updateAISettings",
  async (aiEnabled, { rejectWithValue }) => {
    try {
      const res = await axios.patch(
        `${baseUrl}/user/ai-settings`,
        { aiEnabled },
        getAuthHeader(),
      );

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update AI settings",
      );
    }
  },
);

const queriesAnswerSlice = createSlice({
  name: "queriesAnswer",
  initialState: {
    question: null,
    answers: [],
    loading: false,
    error: null,
    aiEnabled: false,
    aiSettingsLoading: false,
    aiSettingsError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAISettings.pending, (state) => {
        state.aiSettingsLoading = true;
        state.aiSettingsError = null;
      })
      .addCase(fetchAISettings.fulfilled, (state, action) => {
        state.aiSettingsLoading = false;
        state.aiEnabled = Boolean(action.payload.aiEnabled);
      })
      .addCase(fetchAISettings.rejected, (state, action) => {
        state.aiSettingsLoading = false;
        state.aiSettingsError = action.payload;
      })
      .addCase(updateAISettings.pending, (state) => {
        state.aiSettingsLoading = true;
        state.aiSettingsError = null;
      })
      .addCase(updateAISettings.fulfilled, (state, action) => {
        state.aiSettingsLoading = false;
        state.aiEnabled = Boolean(action.payload.aiEnabled);
      })
      .addCase(updateAISettings.rejected, (state, action) => {
        state.aiSettingsLoading = false;
        state.aiSettingsError = action.payload;
      })
      .addCase(fetchQuestionById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchQuestionById.fulfilled, (state, action) => {
        state.loading = false;
        state.question = action.payload;
      })
      .addCase(fetchQuestionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAnswers.fulfilled, (state, action) => {
        state.answers = action.payload;
      })
      .addCase(postAnswer.pending, (state) => {
        state.loading = true;
      })
      .addCase(postAnswer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(postAnswer.fulfilled, (state, action) => {
        state.loading = false;
        state.answers.unshift(action.payload);
      })
      .addCase(likeAnswer.fulfilled, (state, action) => {
        const { answerId } = action.payload;

        const target = state.answers.find((a) => a.answerid === answerId);

        if (target && !target.liked_by_me) {
          target.liked_by_me = 1;
          target.likes_count += 1;
        }
      });
  },
});

export default queriesAnswerSlice.reducer;
